import { CalendarDays, CircleUserRound } from 'lucide-react';

import { getSuggestionDescriptor } from 'components/Kit/EmailSuggestions/utils/suggestionFormatters';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

import { HandledGroupState, QuoteHandledState } from '../types';
import { getHandledTooltipDetails } from '../utils/formatHandledTooltip';

type HandledQuoteRowProps = {
  lane: SuggestedQuoteChange;
  state: HandledGroupState;
  ordinal: string;
  selected: boolean;
  selectionEnabled?: boolean;
  onToggle: () => void;
};

// Read-only handled lane: label + state chip, details on hover or focus. Clicking does nothing.
export function HandledQuoteRow({
  lane,
  state,
  ordinal,
  selected,
  selectionEnabled = true,
  onToggle,
}: HandledQuoteRowProps) {
  const label = getSuggestionDescriptor(lane);
  const tooltipDetails = getHandledTooltipDetails(state, lane);

  return (
    <Tooltip disableHoverableContent>
      <Flex align='center' gap='xs' className='group/handled-row w-full'>
        {selectionEnabled && (
          <div
            className={cn(
              'w-0 shrink-0 -translate-x-1 overflow-hidden opacity-0 transition-all duration-150 group-hover/handled-row:w-5 group-hover/handled-row:translate-x-0 group-hover/handled-row:opacity-100',
              selected && 'w-5 translate-x-0 opacity-100'
            )}
          >
            <input
              type='checkbox'
              checked={selected}
              onChange={onToggle}
              aria-label={`Select ${label}`}
              className='cursor-pointer accent-accent-600'
            />
          </div>
        )}
        <TooltipTrigger asChild>
          <button
            type='button'
            aria-pressed={selectionEnabled ? selected : undefined}
            onClick={selectionEnabled ? onToggle : undefined}
            className={cn(
              'min-w-0 flex-1 cursor-pointer rounded border px-2.5 py-1.5 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-400',
              getHandledRowColorClasses(state)
            )}
          >
            <Flex align='center' gap='xs' className='w-full'>
              <Typography
                variant='body-xxs'
                textColor='muted'
                className='shrink-0 tabular-nums'
              >
                {ordinal}
              </Typography>
              <Typography
                variant='body-xxs'
                className='min-w-0 flex-1 truncate text-neutral-800'
              >
                {label}
              </Typography>
            </Flex>
          </button>
        </TooltipTrigger>
      </Flex>
      <TooltipPortal container={getDrumkitContainer()}>
        {/* Below the row: rows span the taskpane width, so a side tooltip would be clipped. */}
        <TooltipContent
          side='bottom'
          align='start'
          className='pointer-events-none w-fit max-w-[240px] whitespace-nowrap px-2.5 py-2'
        >
          <Flex direction='col' gap='xs' align='start'>
            <Typography variant='body-xs'>{tooltipDetails.headline}</Typography>
            {tooltipDetails.handledByName && (
              <Flex align='center' gap='xs'>
                <CircleUserRound
                  className='h-3 w-3 shrink-0 text-neutral-500'
                  aria-hidden
                />
                <Typography variant='body-xxs'>
                  {tooltipDetails.handledByName}
                </Typography>
              </Flex>
            )}
            {tooltipDetails.handledDate && (
              <Flex align='center' gap='xs'>
                <CalendarDays
                  className='h-3 w-3 shrink-0 text-neutral-500'
                  aria-hidden
                />
                <Typography variant='body-xxs'>
                  {tooltipDetails.handledDate}
                </Typography>
              </Flex>
            )}
          </Flex>
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
}

function getHandledRowColorClasses(state: HandledGroupState): string {
  switch (state) {
    case QuoteHandledState.Replied:
      return 'border-success-300 bg-success-50/40 hover:bg-success-50';
    case QuoteHandledState.Quoted:
      return 'border-warning-400 bg-warning-100 hover:bg-warning-200';
    case QuoteHandledState.Skipped:
      return 'border-neutral-300 bg-neutral-50 hover:bg-neutral-100';
  }
}
